import {useState} from 'react';
import {useRouter} from 'next/router';
import {useToast, InputGroup, InputLeftElement, Input, Button, Text, Flex} from '@chakra-ui/react';
import dayjs from 'dayjs';

import {useTranslate} from '@eksab/i18n';
import {Card} from '@eksab/components';
import {useMeQuery} from '@eksab/hooks';
import {PhoneIcon} from '@eksab/assets/icons';

import type {PackageType} from '@eksab/features/store/types';
import {usePaymentInitiator} from '@eksab/features/payment/hooks';

import {useBuyPackageMutation} from '../hooks';

interface FawryPaymentProps {
  packageId: number;
  code: string;
  type: PackageType;
}

export function FawryPayment({packageId, code, type}: FawryPaymentProps) {
  const {data: me} = useMeQuery();
  const clearPaymentInitiatorURL = usePaymentInitiator((state) => state.clearURL);

  // TODO: Remove country-code deletion when case is handled in endpoint
  const [phoneNumber, setPhoneNumber] = useState(me?.phone_number?.replace('+2', '') ?? '');
  const {push} = useRouter();
  const toast = useToast();

  const t = useTranslate();
  const generateFawryRefCodeMutation = useBuyPackageMutation(
    {
      package_id: packageId,
      package_type: type,
      payment_method: 'FAWRY',
      phone_number: phoneNumber,
      discount_voucher_code: code.length > 0 ? code : undefined,
    },
    {
      onSuccess: (res) => {
        clearPaymentInitiatorURL();

        if (res.next.action === 'add-coins') return;

        toast({title: t('payment.method.fawry.reference-code.success', {phoneNumber}), status: 'success'});
      },
      onError: (e) => toast({title: e.response?.data?.message ?? t('app.errors.try-again'), status: 'error'}),
    },
  );

  const onGetCode = (e: React.FormEvent<HTMLFormElement | HTMLDivElement>) => {
    e.preventDefault();

    mixpanel?.track('Button Clicked', {button: 'payment.fawry.pay_now'});
    fbq?.('track', 'InitiateCheckout');

    const regex = new RegExp('^(01)[0-9]{9}$');
    if (!regex.test(phoneNumber)) return toast({title: t('auth.phone-error'), status: 'error'});

    generateFawryRefCodeMutation.mutate();
  };

  if (generateFawryRefCodeMutation.data?.next.action === 'show-reference-code') {
    return (
      <Flex flexDir="column" align="center" gap="8">
        <Card
          display="flex"
          flexDir="column"
          justifyContent="space-between"
          alignItems="flex-start"
          mt="4"
          p="4"
          w="full"
        >
          <Text fontSize="md">
            {t('payment.method.fawry.reference-code', {
              b: (chunks) => (
                <Text as="span" fontWeight="800">
                  {chunks}
                </Text>
              ),
              code: generateFawryRefCodeMutation.data.next.reference_code,
            })}
          </Text>

          <Text my="4" fontSize="md">
            {t('payment.method.fawry.pay-before', {
              b: (chunks) => (
                <Text as="span" fontWeight="800">
                  {chunks}
                </Text>
              ),
              date: dayjs(generateFawryRefCodeMutation.data.next.expires_at).format('dddd, MMMM D, YYYY hh:mmA'),
            })}
          </Text>
        </Card>

        <Button variant="outline" onClick={() => push('/store/coins')}>
          {t('store.coins.buy-more-coins')}
        </Button>
      </Flex>
    );
  }

  return (
    <Card
      as="form"
      display="flex"
      flexDir="column"
      justifyContent="space-between"
      alignItems="center"
      shadow="xs"
      mt="4"
      px={[6, null, 5]}
      pt="5"
      pb="4"
      w="full"
      onSubmit={onGetCode}
    >
      <Text fontSize="md" lineHeight="1.875">
        {t('payment.method.fawry.description')}
      </Text>

      <InputGroup m="4" mb="5">
        <InputLeftElement pointerEvents="none">
          <PhoneIcon />
        </InputLeftElement>

        <Input
          type="tel"
          autoComplete="tel"
          placeholder={t('app.mobile-number')}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
      </InputGroup>

      <Button w="100%" type="submit" isLoading={generateFawryRefCodeMutation.isLoading}>
        {t('payment.method.fawry.get-code')}
      </Button>

      <Text mt="7" textAlign="center" color="gray.500" fontWeight="700" fontSize="xs">
        {t('payment.method.fawry.notice')}
      </Text>
    </Card>
  );
}
